<template>
  <label
    class="checkbox"
    :class="[{ checkbox_inline: inline }, { '-disabled': disabled }, styleAttrs.class]"
    :style="styleAttrs.style"
  >
    <input
      :checked="value"
      class="checkbox__input"
      type="checkbox"
      :required="required"
      :disabled="disabled"
      v-bind="functionalAttrs"
      @change="onChange"
    />
    <svg class="checkbox__graphic" height="23" width="23" viewBox="0 0 300 300" v-if="boxStyle == 'rounded'">
      <circle
        r="140"
        cx="150"
        cy="150"
        class="checkbox__circle"
        :class="{ 'is-on': value }"
      />
      <lucide-icon
        v-if="value"
        x="60"
        y="65"
        name="check"
        class="checkbox__mark"
        width="190"
        height="190"
      />
    </svg>
    <div v-else class="checkbox__box" :class="{ '-on': value }">
      <lucide-icon
        v-if="value"
        name="check"
        class="checkbox__mark"
      />
    </div>
    <span class="checkbox__label" v-if="label || labelChecked || $slots.label || $slots.default || $slots['label-checked']">
      <slot>
        <template v-if="!labelChecked || !value">
          <slot name="label">
            {{ label }}
          </slot>
        </template>
        <template v-else>
          <slot name="label-checked">
            {{ labelChecked }}
          </slot>
        </template>
      </slot>
    </span>
  </label>
</template>

<script setup lang="ts">
import LucideIcon from "@core/ui/LucideIcon.vue";
import { useAttrsAndStyle } from "@core/composables/attrsAndStyle";

defineOptions({
  name: "InputCheckbox",
  inheritAttrs: false
});

interface Props {
  value: boolean;
  label?: string | null;
  labelChecked?: string | null;
  inline?: boolean;
  required?: boolean;
  disabled?: boolean;
  boxStyle?: "rounded" | "square";
}
const props = withDefaults(defineProps<Props>(), {
  label: null,
  labelChecked: null,
  inline: false,
  required: false,
  disabled: false,
  boxStyle: "square"
});

const emit = defineEmits<{
  (e: "update:value", value: boolean): void;
}>();

const { functionalAttrs, styleAttrs } = useAttrsAndStyle();

function onChange() {
  emit("update:value", !props.value);
}
</script>

<style lang="scss">
.checkbox {
  position: relative;
  display: flex;
  align-items: center;
  --gap: 0.5rem;
  gap: var(--gap);

  .input__container + & {
    margin-top: 0.5em;
  }

  &__input {
    position: absolute;
    opacity: 0;
    left: 0;
    top: calc(50% - 0.75rem);
    height: 1.5rem;
    width: 1.5rem;
    pointer-events: none;
  }

  &__input:focus-visible + &__graphic {
    outline: 2px dotted var(--color-action);
    outline-offset: 2px;
  }

  &__graphic {
    display: block;
    flex-shrink: 0;
    border-radius: 9000px;
    margin-top: -1px;
    height: 23px;
    width: 23px;
  }
  // SVG graphic contents:
  &__circle {
    fill: transparent;
    stroke: var(--color-action);
    stroke-width: 20px;
    transition: fill 125ms;
    &.is-on {
      fill: var(--color-action);
    }
  }
  // end SVG contents

  &__box {
    border-radius: 0.3rem;
    border: 2px solid var(--color-grey-dark);
    background-color: var(--color-background-close);
    width: 1.5em;
    height: 1.5em;
    overflow: hidden;
    flex-shrink: 0;
    text-align: center;
    transition: background-color 125ms, border-color 125ms;
    &.-on {
      background-color: var(--color-action);
      border-color: var(--color-action);
    }
  }
  &__input:focus-visible + &__box {
    outline: 2px dotted var(--color-action);
    outline-offset: 2px;
  }
  &.-disabled &__box {
    border-color: var(--color-grey-light);
    background-color: var(--color-grey-light);
  }

  &__mark {
    color: var(--color-action-contrast);
  }
  &__graphic &__mark {
    font-size: 180px;
  }
  &__box &__mark {
    font-size: 1.2rem;
    margin-top: -1px;
  }

  &__label {
    font-size: var(--font-size-label);
    font-family: var(--font-family-ui);
    display: block;
    width: 100%;
    user-select: none; // this prevents selecting the text when double-clicking on the label.
    text-wrap: balance;
  }

  &_inline {
    display: inline-flex;
  }
}
</style>
